import { useCallback, useEffect, useRef, useState } from "react";
import {
  IconCoffee,
  IconGlass,
  IconToolsKitchen2,
  IconSun,
  IconShadow,
  IconClockHour4,
  IconChevronLeft,
  IconChevronRight,
  IconX,
} from "@tabler/icons-react";

export type CategoryValue = "cafe" | "bar" | "restaurant";
export type SunValue = "sun" | "shade";

export const ALL_CATEGORIES = new Set<CategoryValue>(["cafe", "bar", "restaurant"]);
export const ALL_SUN = new Set<SunValue>(["sun", "shade"]);

type CategoryFilterProps = {
  selectedCategories: Set<CategoryValue>;
  selectedSun: Set<SunValue>;
  openOnly: boolean;
  onToggleCategory: (value: CategoryValue) => void;
  onToggleSun: (value: SunValue) => void;
  onToggleOpen: () => void;
};

const CATEGORIES: { value: CategoryValue; label: string; icon: typeof IconCoffee }[] = [
  { value: "cafe", label: "Cafe", icon: IconCoffee },
  { value: "bar", label: "Bar", icon: IconGlass },
  { value: "restaurant", label: "Restaurant", icon: IconToolsKitchen2 },
];

const SUN_OPTIONS: { value: SunValue; label: string; icon: typeof IconSun }[] = [
  { value: "sun", label: "Sun", icon: IconSun },
  { value: "shade", label: "Shade", icon: IconShadow },
];

function ChipGroup<T extends string>({
  options,
  selected,
  onToggle,
}: {
  options: { value: T; label: string; icon: typeof IconCoffee }[];
  selected: Set<T>;
  onToggle: (v: T) => void;
}) {
  return (
    <>
      {options.map(opt => {
        const isActive = selected.has(opt.value);
        const Icon = opt.icon;
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={isActive}
            onClick={() => onToggle(opt.value)}
            className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition ${
              isActive
                ? "border-amber-400 bg-amber-50 text-amber-700"
                : "border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-700"
            }`}
          >
            <Icon size={13} stroke={2} />
            {opt.label}
            {isActive ? (
              <span
                role="button"
                tabIndex={0}
                aria-label={`${opt.label} entfernen`}
                onClick={e => {
                  e.stopPropagation();
                  onToggle(opt.value);
                }}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    onToggle(opt.value);
                  }
                }}
                className="ml-0.5 inline-flex rounded-full p-0.5 transition hover:bg-amber-200/60"
              >
                <IconX size={11} stroke={2.5} />
              </span>
            ) : null}
          </button>
        );
      })}
    </>
  );
}

export function CategoryFilter({
  selectedCategories,
  selectedSun,
  openOnly,
  onToggleCategory,
  onToggleSun,
  onToggleOpen,
}: CategoryFilterProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    updateScrollState();
    window.addEventListener("resize", updateScrollState);
    return () => window.removeEventListener("resize", updateScrollState);
  }, [updateScrollState]);

  const scrollBy = useCallback((delta: number) => {
    scrollRef.current?.scrollBy({ left: delta, behavior: "smooth" });
  }, []);

  const boxClass =
    "flex shrink-0 items-center gap-1.5 rounded-full border border-slate-200/80 bg-white/95 px-2 py-1.5 shadow-lg backdrop-blur";

  return (
    <div className="relative">
      {canScrollLeft ? (
        <button
          type="button"
          aria-label="Scroll filters left"
          onClick={() => scrollBy(-120)}
          className="absolute left-0 top-1/2 z-10 -translate-y-1/2 inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-md transition hover:bg-slate-50"
        >
          <IconChevronLeft size={16} stroke={2} />
        </button>
      ) : null}

      <div
        ref={scrollRef}
        onScroll={updateScrollState}
        className="no-scrollbar flex items-center gap-2 overflow-x-auto"
      >
        <div className={boxClass}>
          <ChipGroup options={SUN_OPTIONS} selected={selectedSun} onToggle={onToggleSun} />
        </div>
        {/* <div className={boxClass}>
          <button
            type="button"
            aria-pressed={openOnly}
            onClick={onToggleOpen}
            className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition ${
              openOnly
                ? "border-amber-400 bg-amber-50 text-amber-700"
                : "border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-700"
            }`}
          >
            <IconClockHour4 size={13} stroke={2} />
            Open
          </button>
        </div> */}
        <div className={boxClass}>
          <ChipGroup options={CATEGORIES} selected={selectedCategories} onToggle={onToggleCategory} />
        </div>
      </div>

      {canScrollRight ? (
        <button
          type="button"
          aria-label="Scroll filters right"
          onClick={() => scrollBy(120)}
          className="absolute right-0 top-1/2 z-10 -translate-y-1/2 inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-md transition hover:bg-slate-50"
        >
          <IconChevronRight size={16} stroke={2} />
        </button>
      ) : null}
    </div>
  );
}
